import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../lib/utils';
import { FileText, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function Applications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const listingId = searchParams.get('listing');
  
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewApplication, setShowNewApplication] = useState(!!listingId);
  const [listing, setListing] = useState(null);
  
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    date_of_birth: '',
    employment_status: 'employed',
    employer_name: '',
    annual_income: '',
    move_in_date: '',
    term_months: 12
  });

  useEffect(() => {
    loadApplications();
    if (listingId) {
      loadListing(listingId);
    }
  }, [listingId]);

  const loadApplications = async () => {
    try {
      // Use different endpoint based on user role
      const endpoint = user?.role === 'tenant' ? '/applications' : '/applications/landlord/list';
      const { data } = await api.get(endpoint);
      setApplications(data);
    } catch (error) {
      console.error('Failed to load applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const loadListing = async (id) => {
    try {
      const { data } = await api.get(`/listings/${id}`);
      setListing(data);
    } catch (error) {
      toast.error('Failed to load listing');
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    
    if (!listing || !listing.property_id) {
      toast.error('Property information missing');
      return;
    }
    
    try {
      const applicationData = {
        listing_id: listingId,
        property_id: listing.property_id,
        move_in_date: formData.move_in_date,
        term_months: parseInt(formData.term_months)
      };

      await api.post('/applications', applicationData);
      toast.success('Application submitted successfully!');
      setShowNewApplication(false);
      loadApplications();
      navigate('/tenant/applications');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  };

  const handleUpdateStatus = async (applicationId, newStatus) => {
    try {
      await api.put(`/applications/${applicationId}/status?status=${newStatus}`);
      toast.success('Application status updated');
      loadApplications();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'rejected': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'submitted': return <Clock className="w-5 h-5 text-blue-600" />;
      case 'in_review': return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      default: return <FileText className="w-5 h-5 text-slate-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'in_review': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const isTenant = user?.role === 'tenant';

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-display font-bold text-slate-900 mb-2">
              {isTenant ? 'My Applications' : 'Tenant Applications'}
            </h1>
            <p className="text-lg text-slate-600">
              {isTenant ? 'Track your rental applications' : 'Review and manage applications'}
            </p>
          </div>
        </div>

        {/* New Application Dialog */}
        {isTenant && showNewApplication && listing && (
          <Dialog open={showNewApplication} onOpenChange={setShowNewApplication}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-display font-bold">Apply for Property</DialogTitle>
                <p className="text-slate-600">{listing.title}</p>
              </DialogHeader>
              
              <form onSubmit={handleSubmitApplication} className="space-y-6 mt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label>Employment Status</Label>
                  <Select value={formData.employment_status} onValueChange={(value) => setFormData({...formData, employment_status: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employed">Employed</SelectItem>
                      <SelectItem value="self_employed">Self Employed</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                      <SelectItem value="unemployed">Unemployed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Employer Name</Label>
                  <Input
                    value={formData.employer_name}
                    onChange={(e) => setFormData({...formData, employer_name: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Annual Gross Income (£)</Label>
                  <Input
                    type="number"
                    value={formData.annual_income}
                    onChange={(e) => setFormData({...formData, annual_income: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label>Preferred Move-in Date</Label>
                  <Input
                    type="date"
                    value={formData.move_in_date}
                    onChange={(e) => setFormData({...formData, move_in_date: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Tenancy Length (months)</Label>
                  <Input
                    type="number"
                    value={formData.term_months}
                    onChange={(e) => setFormData({...formData, term_months: e.target.value})}
                    min={6}
                    required
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <Button type="submit" className="flex-1">Submit Application</Button>
                  <Button type="button" variant="outline" onClick={() => setShowNewApplication(false)}>Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Applications List */}
        {loading ? (
          <div className="text-center py-12">Loading applications...</div>
        ) : applications.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold mb-2">No applications yet</h3>
            <p className="text-slate-600 mb-6">
              {isTenant ? 'Start by browsing available properties' : 'Applications will appear here once tenants apply'}
            </p>
            {isTenant && (
              <Button onClick={() => navigate('/properties')}>Browse Properties</Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((application) => (
              <div key={application.id} className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4 flex-1">
                    {getStatusIcon(application.status)}
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-1">
                        Application for Property
                      </h3>
                      <p className="text-slate-600 mb-2">
                        Submitted {formatDate(application.submitted_at || application.created_at)}
                      </p>
                      <div className="flex flex-wrap gap-3 text-sm">
                        <span className="text-slate-600">
                          <strong>Listing ID:</strong> {application.listing_id?.substring(0, 8)}...
                        </span>
                        {application.move_in_date && (
                          <span className="text-slate-600">
                            <strong>Move-in:</strong> {formatDate(application.move_in_date)}
                          </span>
                        )}
                        <span className="text-slate-600">
                          <strong>Term:</strong> {application.term_months} months
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(application.status)}`}>
                    {application.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                {!isTenant && application.status === 'submitted' && (
                  <div className="flex gap-3 mt-4 pt-4 border-t">
                    <Button 
                      size="sm" 
                      onClick={() => handleUpdateStatus(application.id, 'in_review')}
                      variant="outline"
                    >
                      Review
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => handleUpdateStatus(application.id, 'approved')}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Approve
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => handleUpdateStatus(application.id, 'rejected')}
                      variant="destructive"
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}